import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ProductService } from '../../service/product.service';
import { User } from '../../../user/models/user.type';
import { UserService } from '../../../user/services/user.service';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Accordion, AccordionPanel, AccordionHeader, AccordionContent } from 'primeng/accordion';
import { TippyDirective } from '@ngneat/helipopper';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { CarouselModule } from 'primeng/carousel';
import { GalleriaModule } from 'primeng/galleria';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { CategoryService } from '../../../import/service/category.service';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { EditAttributeComponent } from './edit-attribute/edit-attribute.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ShopService } from '../../../shop/service/shop.service';
import { MessageService } from 'primeng/api';
import { ChannelService } from '../../../import/service/channel.service';
import { ToastModule } from 'primeng/toast';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TreeSelectModule } from 'primeng/treeselect';
import { EditAttributeMediaSellerComponent } from './edit-attribute-media-seller/edit-attribute-media-seller.component';
import { EditAttributeTypologySellerComponent } from './edit-attribute-typology-seller/edit-attribute-typology-seller.component';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-product-attributes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CardModule,
    TranslateModule,
    ButtonModule,
    Accordion, AccordionPanel, AccordionHeader, AccordionContent,
    TippyDirective,
    ScrollPanelModule,
    CarouselModule,
    GalleriaModule,
    ImageModule,
    InputTextModule,
    TagModule,
    DividerModule,
    FileUploadModule,
    TextareaModule,
    SelectModule,
    ToastModule,
    SkeletonModule,
    ProgressSpinnerModule,
    TreeSelectModule,
    TooltipModule,
    DialogModule
  ],
  templateUrl: './product-attributes.component.html',
  styleUrl: './product-attributes.component.scss',
  providers: [DialogService, MessageService],
})
export class ProductAttributesComponent implements OnInit {
  formObligatory: FormGroup = new FormGroup({});
  formMedia: FormGroup = new FormGroup({});
  formRecommended: FormGroup = new FormGroup({});
  formSecondary: FormGroup = new FormGroup({});

  obligatoryAttributes = [];
  recommendedAttributes = [];
  secondaryAttributes = [];
  mediaAttributes = [];

  user: User;
  images;
  displayGalleria: boolean = false;

  @Input() product;
  @Input() disabled: boolean = false;
  @Input() isMasterProduct: boolean = false;
  @Input() categories;
  @Input() operator;
  @Output() update = new EventEmitter();

  attributes = [];
  errors = {};

  tabs = [];
  showButtons: boolean = false;
  ref: DynamicDialogRef | undefined;
  shops;
  shop;
  selectedShop;
  selectedChannel;
  activeIndex: number[] = [0];
  
  constructor(
    private readonly translate: TranslateService,
    private readonly productService: ProductService,
    private readonly userService: UserService,
    private readonly categoryService: CategoryService,
    private readonly dialogService: DialogService,
    private readonly shopService: ShopService,
    private readonly messageService: MessageService,
    private readonly channelService: ChannelService,
  ) {}

  ngOnInit(): void {
    this.user = this.userService.getUser();
    this.fetchAndInitializeProductAttributes();
  }

  fetchAndInitializeProductAttributes(product = null) {
    if (product != null) {
      this.product = product;
    }
    this.getDefaultInfo();

    const locales = this.channelService.getSavedLocales();
    const locale = this.channelService.getSelectedLocale();
    this.images = this.product?.images;
    this.categoryService
      .getAttributes(this.product?.category?.categoryId, locales, locale)
      .subscribe({
        next: (res) => {
          this.assignAttributes(res[0]);
          this.initializeForms();
        },
      });
  }

  getDefaultInfo() {
    if (this.userService.hasRole(['ROLE_OPERATOR', 'ROLE_OPERATOR_ADMIN'])) {
      this.selectedChannel = this.channelService.getSelectedChannel();
    } else {
      const channels = this.channelService.getSavedChannels();
      this.shops = this.user?.relatedShops;
      const code = this.shopService.getSelectedShopCode();
      this.selectedShop = this.shops?.find((shop) => shop.shopCode === code);
      this.selectedChannel = channels.find((c) => c.channel === this.selectedShop?.shopChannel.channelCode);
    }
  }

  assignAttributes(attributes: any) {
    let typologyAndEan = attributes['OPTIONAL'].filter(
      (att) =>
        att.attributeCode === 'Typology' || att.attributeCode === 'EANs/EAN',
    );
    this.obligatoryAttributes = attributes['REQUIRED'].concat(typologyAndEan);
    this.secondaryAttributes = attributes['OPTIONAL'].filter(
      (att) =>
        att.attributeCode != 'Typology' && att.attributeCode != 'EANs/EAN',
    );
    this.mediaAttributes = attributes['MEDIA'];
    const order = { 'IMAGE': 1, 'PDF': 2 };
    this.mediaAttributes.sort((a, b) => {
      return (order[a.attributeType] || 0) - (order[b.attributeType] || 0);
    });
    this.recommendedAttributes = attributes['RECOMMENDED'];
  }

  initializeForms() {
    this.formObligatory = this.createForms(
      this.obligatoryAttributes,
      'REQUIRED',
    );
    this.formMedia = this.createForms(this.mediaAttributes, 'MEDIA');
    this.formSecondary = this.createForms(this.secondaryAttributes, 'OPTIONAL');
    this.formRecommended = this.createForms(
      this.recommendedAttributes,
      'RECOMMENDED',
    );
    this.updateTabs();
  }

  updateTabs() {
    this.tabs = [
      {
        header: 'detailProduct.obligatoryAttributes',
        type: 'obligatory',
        form: this.formObligatory,
        attributes: this.obligatoryAttributes,
        class: 'pi pi-flag',
        errors: this.errors['REQUIRED'],
      },
      {
        header: 'detailProduct.mediaAttributes',
        type: 'media',
        form: this.formMedia,
        attributes: this.mediaAttributes,
        class: 'pi pi-images',
        errors: this.errors['MEDIA'],
      },
      {
        header: 'detailProduct.recommendedAttributes',
        type: 'recommended',
        form: this.formRecommended,
        attributes: this.recommendedAttributes,
        errors: this.errors['RECOMMENDED'],
        class: 'pi pi-thumbs-up',
      },
      {
        header: 'detailProduct.secondaryAttributes',
        type: 'secondary',
        form: this.formSecondary,
        attributes: this.secondaryAttributes,
        class: 'pi pi-plus',
        errors: this.errors['OPTIONAL'],
      },
    ];
  }

  handleInput(form, attribute) {
    if (this.user.operator) {
      const sellersId = this.product.sellerProducts.map(
        (product) => product.productId,
      );
      this.handleOperatorInput(attribute, sellersId);
    } else if (attribute.attributeType === 'MEDIA' || attribute.attributeType === 'PDF') {
      this.handleSellerMedia(attribute);
    } else if (attribute.attributeCode === 'Typology') {
      this.handleSellerTypologyEdit(
        attribute,
        form.controls[attribute.attributeCode]?.value,
      );
    } else {
      this.showButtons = true;
      form.controls[attribute.attributeCode].enable();
    }
  }

  handleSellerTypologyEdit(attribute, selected) {
    this.openDialog(
      EditAttributeTypologySellerComponent,
      {
        attribute: attribute,
        currentProduct: this.product,
        categories: this.categories,
        selectedCategory: selected,
      },
      '45vw',
      '50vh'
    );
  }

  handleSellerMedia(attribute) {
    this.openDialog(
      EditAttributeMediaSellerComponent,
      { 
        attribute: attribute, 
        currentProduct: this.product 
      },
      '45vw',
      '50vh'
    );
  }

  handleOperatorInput(attribute, sellersId) {
    this.openDialog(
      EditAttributeComponent,
      {
        attribute: attribute,
        ids: sellersId,
        masterProduct: this.product,
        categories: this.categories,
        selectedChannel: this.selectedChannel,
      },
      '50vw',
      '75vh'
    );
  }

  openDialog(component, data, width, height) {
    this.ref = this.dialogService.open(component, {
      header:  this.translate.instant('alerts.updateAttibute'),
      data: data,
      focusOnShow: false,
      closable: true,
      width: width,
      height: height,
      modal: true,
      breakpoints: {
        '960px': component === EditAttributeComponent ? '75vw' : '60vw',
        '640px': component === EditAttributeComponent ? '90vw' : '75vw',
      },
    });
    
    this.ref.onClose.subscribe((result) => this.handleDialogClose(result));
  }

  handleDialogClose(result) {
    if (result === 'success') {
      this.showSuccessMessage();
      setTimeout(() => {
        this.update.emit('refresh');
      }, 1000);
    } else if (result === 'error') {
      this.showErrorMessage();
    }
    this.fetchAndInitializeProductAttributes();
  }

  showSuccessMessage() {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('alerts.successSummary'),
      detail: this.translate.instant('alerts.success'),
    });
  }

  showErrorMessage() {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('alerts.errorSummary'),
      detail: this.translate.instant('alerts.error'),
    });
  }

  resetAllForms() {
    this.formObligatory.disable();
    this.formMedia.disable();
    this.formRecommended.disable();
    this.formSecondary.disable();
  }

  createForms(attributes, type) {
    const group = {};
    let error = 'valid';
    let invalidStatusFound = false;
    let awaintingDownloadStatusFound = false;

    if (!attributes?.length) {
      this.errors[type] = error;
      return new FormGroup(group);
    }

    for (let attribute of attributes) {
      const attr = this.findProductAttribute(attribute);
      this.updateAttributeMetadata(attribute, attr);

      if (attribute.status?.toLowerCase() === 'invalid') {
        invalidStatusFound = true;
      }

      if(attribute.status?.toLowerCase() === 'awaiting_download') {
        awaintingDownloadStatusFound = true;
      }

      group[attribute.attributeCode] = this.createFormControlForAttribute(
        attribute,
        attr,
      );
    }
    if(invalidStatusFound) {
      error = 'invalid'
    } else if (awaintingDownloadStatusFound) {
      error = 'awaiting'
    }
    this.errors[type] = error;
    return new FormGroup(group);
  }

  private findProductAttribute(attribute) {
    return (
      this.product.attributes.find(
        (attr) =>
          attr.codeAttributes.toLowerCase().trim() ==
          attribute.attributeCode.toLowerCase().trim(),
      ) ?? null
    );
  }

  private updateAttributeMetadata(attribute, attr) {
    attribute.status = attr?.status;
    attribute.tag = attr?.tag;
    attribute.referenced = attr?.referenced;
    attribute.errorMessage = attr?.errorMessage;
    attribute.showMessage = false;
    attribute.maximized = false;
  }

  private createFormControlForAttribute(attribute, attr) {
    if (this.isTypologyForNonOperator(attribute)) {
      const value = this.findCategoryByName(this.categories, attr?.value);
      return new FormControl(
        {
          value: value,
          disabled: this.disabled,
        },
        [Validators.required],
      );
    }

    if (attribute.attributeType === 'LIST') {
      return this.createListFormControl(attribute, attr);
    }

    if (attribute.attributeCode === 'EANs/EAN') {
      return new FormControl(
        {
          value: attr?.value ?? '',
          disabled: this.disabled,
        },
        [
          Validators.required,
          Validators.pattern(/^\d{13}(?: \d{13})*$/),
          duplicateEANValidator(),
        ]
      );
    }
    
    return this.createStandardFormControl(attribute, attr);
  }

  private isTypologyForNonOperator(attribute) {
    return attribute.attributeCode === 'Typology' && !this.operator;
  }

  private createListFormControl(attribute, attr) {
    const value = attribute.options?.filter(
      (option) => option.code === attr?.codeValue,
    );
    return new FormControl({
      value: value?.[0] ?? '',
      disabled: this.disabled,
    });
  }

  private createStandardFormControl(attribute, attr) {
    const isNumeric =
      attribute.attributeType == 'NUMBER' ||
      attribute.attributeType == 'INTEGER';
    const validators = isNumeric
      ? [Validators.pattern(String.raw`^-?\d*(\.|,)?\d+$`)]
      : [];

    return new FormControl(
      {
        value: attr?.value ?? '',
        disabled: this.disabled,
      },
      validators,
    );
  }

  findCategoryByName(categoriesArray, searchLabel: string) {
    function searchInNode(categoryData) {
      // Check if current node matches
      const isMatch = categoryData.categoryBreadcrumbLoc === searchLabel;
      if (isMatch) {
        return categoryData;
      }

      // Search in children if they exist
      if (categoryData.hasChildren && categoryData.children) {
        for (const child of categoryData.children) {
          const result = searchInNode(child);
          if (result) {
            return result;
          }
        }
      }

      return null;
    }

    // Search through the array of categories
    for (const category of categoriesArray) {
      const result = searchInNode(category);
      if (result) {
        return result;
      }
    }

    return null;
  }

  openAllTabs() {
    this.activeIndex = [0, 1, 2, 3];
  }

  displayImage() {
    this.displayGalleria = true;
  }

  hideImage() {
    this.displayGalleria = false;
  }

  sendAttributes() {
    const attributes = this.collectModifiedAttributes();

    const { sku, ean, displayName } = this.extractSpecialAttributes(attributes);

    if (this.hasMandatoryAttributeErrors(sku, ean, displayName)) {
      this.showMandatoryAttributesError();
      return;
    }

    this.submitAttributeUpdates(attributes, sku, ean);
  }

  private collectModifiedAttributes() {
    const attributes = [];

    for (const tab of this.tabs) {
      if (!tab.attributes) continue;

      for (const attribute of tab.attributes) {
        if (!tab.form.controls[attribute.attributeCode].dirty) continue;

        const { value, code } = this.extractAttributeValue(tab, attribute);

        attributes.push({
          attributeCode: attribute.attributeCode,
          localized: attribute.localized,
          project: attribute.locale,
          attributeValue: value,
          attributeCodeValue: code,
        });
      }
    }

    return attributes;
  }

  private extractAttributeValue(tab, attribute) {
    let value = tab.form.controls[attribute.attributeCode].value;
    let code = null;

    if (attribute.attributeType === 'LIST') {
      value = tab.form.controls[attribute.attributeCode].value.label;
      code = tab.form.controls[attribute.attributeCode].value.code;
    } else if (
      attribute.attributeCode === 'Typology' &&
      this.userService.isSeller()
    ) {
      value =
        tab.form.controls[attribute.attributeCode].value.categoryBreadcrumbLoc;
      code = tab.form.controls[attribute.attributeCode].value.categoryId;
    }

    return { value, code };
  }

  private extractSpecialAttributes(attributes) {
    return {
      sku: attributes.find(
        (attribute) => attribute.attributeCode === 'SKU_PART',
      ),
      ean: attributes.find(
        (attribute) => attribute.attributeCode === 'EANs/EAN',
      ),
      displayName: attributes.find((attribute) =>
        attribute.attributeCode.startsWith('DisplayName'),
      ),
    };
  }

  private hasMandatoryAttributeErrors(sku, ean, displayName) {
    return (
      ean?.attributeValue === '' ||
      sku?.attributeValue === '' ||
      displayName?.attributeValue === ''
    );
  }

  private showMandatoryAttributesError() {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('alerts.errorSummary'),
      detail: this.translate.instant('alerts.errorMandatoryAttributes'),
    });
  }

  private submitAttributeUpdates(attributes, sku, ean) {
    if (attributes?.length === 0) return;

    const data = {
      productId: this.product.productId,
      shopCode: this.shopService.getSelectedShop().shopCode,
      shopName: this.shopService.getSelectedShop().shopName,
      eans: ean ? [ean.attributeValue] : this.product.ean,
      sku: sku ? sku.attributeValue : this.product.sku,
      user: this.userService.getUser().email,
      attributesModifications: attributes,
      categoryBreadCrumb: this.product.category.categoryBreadCrumb,
    };

    this.showButtons = false;
    this.resetAllForms();

    this.productService.updateProduct(data).subscribe({
      next: () => this.handleUpdateSuccess(),
      error: () => this.handleUpdateError(),
      complete: () => {
        this.update.emit('refresh');
      },
    });
  }

  private handleUpdateSuccess() {
    this.messageService.clear();
    this.showSuccessMessage();
  }

  private handleUpdateError() {
    this.messageService.clear();
    this.showErrorMessage();
  }

  cancelEdit() {
    this.showButtons = false;
    this.fetchAndInitializeProductAttributes();
  }
}

export function duplicateEANValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value: string = control.value;
    if (!value) {
      return null; // let the required validator handle empty values
    }
    const eanList = value.split(' ').filter(ean => ean.trim() !== '');
    const duplicates = eanList.filter(
      (ean, index) => eanList.indexOf(ean) !== index
    );
    return duplicates.length > 0 ? { duplicateEAN: 'Duplicate EAN found' } : null;
  };
}